import { cn } from 'cn';
import { memo, useState } from 'react';

import { type UUID } from '~/types/common';

import { useQueryCompanyLogo } from '~/data/company';

import { Icon, Spinner } from '@vestigas/ui';

type LogoSize = 'xs' | 'sm' | 'md' | 'lg';
type LogoAspectRatio = 'square' | 'wide';
type P = {
  readonly aspectRatio?: LogoAspectRatio;
  readonly companyId?: UUID;
  readonly companyLogoSrc?: string; // base64-encoded image
  readonly fallbackVariant?: 'default' | 'tonal';
  readonly logoUrl?: string | null; // direct URL to the logo image
  readonly size?: LogoSize;
  readonly testId?: string;
} & ComponentStyling;

const aspectRatioClasses: Record<LogoAspectRatio, string> = {
  square: 'aspect-square',
  wide: 'aspect-2/1',
};

const heightClasses: Record<LogoSize, string> = {
  lg: 'h-32',
  md: 'h-24',
  sm: 'h-16',
  xs: 'h-8',
};

const sizeClassesFallbackIcon = {
  lg: 'size-16',
  md: 'size-12',
  sm: 'size-8',
  xs: 'size-4',
} as const;

function LogoLoadingOverlay({ isLoading }: { readonly isLoading: boolean }) {
  if (!isLoading) {
    return null;
  }

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center opacity-100 backdrop-blur transition-all duration-300 ease-out">
      <Spinner initialDelay={600} />
    </div>
  );
}

function LogoFallback({
  fallbackVariant,
  isLogoLoaded,
  size,
}: {
  readonly fallbackVariant: 'default' | 'tonal';
  readonly isLogoLoaded: boolean;
  readonly size: LogoSize;
}) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out',
        fallbackVariant === 'tonal' && 'bg-tiga-surface-off-white',
        isLogoLoaded ? 'opacity-0' : 'opacity-100',
      )}
    >
      <Icon
        className={cn(
          fallbackVariant === 'tonal'
            ? 'text-tiga-text-lighter'
            : 'text-tiga-text-lightest',
          sizeClassesFallbackIcon[size],
        )}
        metaphor="company"
      />
    </div>
  );
}

function LogoImage({
  isLogoLoaded,
  onError,
  resolvedSrc,
  size,
}: {
  readonly isLogoLoaded: boolean;
  readonly onError: (src: string) => void;
  readonly resolvedSrc?: string;
  readonly size: LogoSize;
}) {
  if (!isLogoLoaded || !resolvedSrc) {
    return null;
  }

  return (
    <img
      alt="company logo"
      className={cn(
        'absolute inset-0 size-full rounded-md object-contain transition-opacity duration-100 ease-out',
        {
          'p-0.5': size === 'xs',
          'p-1': size === 'sm',
          'p-2': size === 'md' || size === 'lg',
        },
      )}
      onError={() => onError(resolvedSrc)}
      src={resolvedSrc}
    />
  );
}

/**
 * Company logo component that fetches and displays a company's logo.
 * Supports loading a logo from a direct URL (e.g. Azure Blob Storage) or a base64-encoded image.
 * Uses the now legacy API endpoint for fetching the logo from the company ID.
 * Uses the direct URL or base64-encoded image if provided, otherwise falls back to the legacy API.
 * All three states (loading, logo, fallback) are rendered as stacked layers
 * with opacity transitions for smooth state changes.
 */
export const CompanyLogo = memo(function CompanyLogo({
  aspectRatio = 'square',
  className,
  companyId,
  companyLogoSrc,
  fallbackVariant = 'default',
  logoUrl,
  size = 'sm',
  style,
  testId,
}: P) {
  // Tracks the specific src that failed to load, so a later valid src gets a fresh attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const hasExternalSrc = Boolean(logoUrl) || Boolean(companyLogoSrc);

  const { data: companyLogo, isLoading } = useQueryCompanyLogo(
    companyId ?? '',
    {
      enabled: Boolean(companyId) && !(hasExternalSrc || logoUrl === null), // null means the logoUrl prop is used, but we don't have a logo -> pointless to fetch via API
    },
  );

  const resolvedSrc = logoUrl || companyLogoSrc || companyLogo;
  const hasImageError = Boolean(resolvedSrc) && resolvedSrc === failedSrc;

  const isLogoLoaded =
    (Boolean(companyLogo) || hasExternalSrc) && !hasImageError;

  return (
    <div
      data-testid={testId}
      className={cn(
        'relative shrink-0 rounded-lg bg-white p-1 contain-paint',
        aspectRatioClasses[aspectRatio],
        heightClasses[size],
        className,
      )}
      style={style}
    >
      <LogoLoadingOverlay isLoading={isLoading} />
      <LogoFallback
        fallbackVariant={fallbackVariant}
        isLogoLoaded={isLogoLoaded}
        size={size}
      />
      <LogoImage
        isLogoLoaded={isLogoLoaded}
        resolvedSrc={resolvedSrc}
        size={size}
        onError={setFailedSrc}
      />
    </div>
  );
});
